import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { Volume2, Square, Settings2 } from "lucide-react";

const Ctx = createContext(null);

// Snelheid = SpeechSynthesis "rate" (1 = normaal).
export const SPEED_OPTIONS = [
  { value: 0.8, label: "Langzaam" },
  { value: 1.0, label: "Normaal" },
  { value: 1.2, label: "Snel" },
];

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

// Bekende native Nederlandse vrouwenstemmen (iOS/macOS/Windows/Android/Chrome).
const FEMALE_HINTS = [
  "ellen", "fenna", "maartje", "claire", "lotte", "saskia", "femke",
  "colette", "laura", "google nederlands", "nederlands (nederland)",
];

const isDutch = (v) => /^nl(\b|[-_])/i.test(v.lang || "");
const isNL = (v) => /^nl[-_]nl$/i.test(v.lang || "") || /nederland/i.test(v.name || "");
const looksFemale = (v) => FEMALE_HINTS.some((h) => (v.name || "").toLowerCase().includes(h));

function pickDefaultVoice(nlVoices) {
  if (!nlVoices.length) return null;
  return (
    nlVoices.find((v) => isNL(v) && looksFemale(v)) ||
    nlVoices.find((v) => looksFemale(v)) ||
    nlVoices.find((v) => isNL(v)) ||
    nlVoices[0]
  );
}

export function tokenize(text) {
  return (text || "")
    .split(/(\s+)/)
    .filter((s) => s.length)
    .map((s) => ({ w: s, isWord: !/^\s+$/.test(s) }));
}

// Absolute begin-offset (in tekenposities) van elk WOORD, in volgorde.
function wordStarts(text) {
  const out = [];
  let offset = 0;
  for (const t of tokenize(text)) {
    if (t.isWord) out.push(offset);
    offset += t.w.length;
  }
  return out;
}

// Splits in zin-achtige stukken met behoud van absolute offset.
// Korte stukken vermijden de Chrome ~15s-afkapbug en houden de sync strak.
function splitChunks(text) {
  const chunks = [];
  const re = /[^.!?…]+[.!?…]*\s*/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m[0].trim()) chunks.push({ text: m[0], offset: m.index });
  }
  if (!chunks.length && text) chunks.push({ text, offset: 0 });
  return chunks;
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export function ReadAloudProvider({ children }) {
  const [activeKey, setActiveKey] = useState(null);
  const [wordIndex, setWordIndex] = useState(-1);
  const [loadingKey, setLoadingKey] = useState(null);
  const [group, setGroup] = useState(null);
  const [voices, setVoices] = useState([]);

  const voiceURIRef = useRef(localStorage.getItem("la_tts_voice_uri") || "");
  const speedRef = useRef(parseFloat(localStorage.getItem("la_tts_rate")) || 1.0);
  const [voiceURI, setVoiceURIs] = useState(voiceURIRef.current);
  const [speed, setSpeedS] = useState(speedRef.current);

  const setVoiceURI = useCallback((uri) => {
    voiceURIRef.current = uri; setVoiceURIs(uri); localStorage.setItem("la_tts_voice_uri", uri);
  }, []);
  const setSpeed = useCallback((s) => {
    speedRef.current = s; setSpeedS(s); localStorage.setItem("la_tts_rate", String(s));
  }, []);

  const seqRef = useRef(0);

  // Laad beschikbare stemmen en kies een Nederlandse vrouwenstem als standaard.
  useEffect(() => {
    if (!synth) return;
    const load = () => {
      const nl = (synth.getVoices() || []).filter(isDutch);
      setVoices(nl);
      const has = voiceURIRef.current && nl.some((v) => v.voiceURI === voiceURIRef.current);
      if (!has) {
        const def = pickDefaultVoice(nl);
        if (def) setVoiceURI(def.voiceURI);
      }
    };
    load();
    synth.onvoiceschanged = load;
    return () => { if (synth) synth.onvoiceschanged = null; };
  }, [setVoiceURI]);

  const selectedVoice = useCallback(() => {
    const all = synth ? synth.getVoices() : [];
    return all.find((v) => v.voiceURI === voiceURIRef.current) || pickDefaultVoice(all.filter(isDutch));
  }, []);

  const stop = useCallback(() => {
    seqRef.current++;
    if (synth) synth.cancel();
    setActiveKey(null); setWordIndex(-1); setLoadingKey(null); setGroup(null);
  }, []);

  useEffect(() => () => { seqRef.current++; if (synth) synth.cancel(); }, []);

  const speakChunk = useCallback((chunk, starts, mySeq) =>
    new Promise((resolve) => {
      const u = new SpeechSynthesisUtterance(chunk.text);
      u.lang = "nl-NL";
      const v = selectedVoice();
      if (v) u.voice = v;
      u.rate = speedRef.current;
      u.pitch = 1;
      u.onboundary = (e) => {
        if (seqRef.current !== mySeq) return;
        if (e.name && e.name !== "word") return;
        const ci = (e.charIndex || 0) + chunk.offset;
        let idx = 0;
        for (let i = 0; i < starts.length; i++) { if (starts[i] <= ci) idx = i; else break; }
        setWordIndex(idx);
      };
      u.onend = () => resolve();
      u.onerror = () => resolve();
      synth.speak(u);
    }), [selectedVoice]);

  const speakSegment = useCallback(async (seg, mySeq) => {
    const starts = wordStarts(seg.text);
    setActiveKey(seg.key);
    setWordIndex(0);
    setLoadingKey(null);
    for (const chunk of splitChunks(seg.text)) {
      if (seqRef.current !== mySeq) return;
      await speakChunk(chunk, starts, mySeq);
    }
  }, [speakChunk]);

  const speak = useCallback(async (segments, groupId) => {
    if (!synth) return;
    stop();
    await delay(60); // laat cancel() bezinken voordat we opnieuw starten
    const mySeq = ++seqRef.current;
    setGroup(groupId || "default");
    for (const seg of segments) {
      if (seqRef.current !== mySeq) return;
      if (!seg.text || !seg.text.trim()) continue;
      await speakSegment(seg, mySeq);
      if (seqRef.current !== mySeq) return;
    }
    if (seqRef.current === mySeq) {
      setActiveKey(null); setWordIndex(-1); setLoadingKey(null); setGroup(null);
    }
  }, [stop, speakSegment]);

  return (
    <Ctx.Provider value={{ speak, stop, activeKey, wordIndex, loadingKey, group, voices, voiceURI, speed, setVoiceURI, setSpeed }}>
      {children}
    </Ctx.Provider>
  );
}

export const useReadAloud = () => useContext(Ctx) || {};

/** Renders text, highlighting the spoken word when this segment is active. */
export function SpokenText({ segKey, text, as: Tag = "span", className = "", style, ...rest }) {
  const { activeKey, wordIndex } = useReadAloud();
  const active = activeKey === segKey;
  if (!active) return <Tag className={className} style={style} {...rest}>{text}</Tag>;
  const tokens = tokenize(text);
  let wi = -1;
  return (
    <Tag className={className} style={style} {...rest}>
      {tokens.map((t, i) => {
        if (!t.isWord) return <span key={i}>{t.w}</span>;
        wi++;
        return <span key={i} className={wi === wordIndex ? "ra-active" : undefined}>{t.w}</span>;
      })}
    </Tag>
  );
}

/** Speaker button that plays a list of {key, text} segments. */
export function ReadButton({ segments, groupId, className = "", label, "data-testid": testId }) {
  const { speak, stop, group } = useReadAloud() || {};
  const isThis = group === groupId;
  const onClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (isThis) stop(); else speak(segments, groupId);
  };
  const Icon = isThis ? Square : Volume2;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={isThis ? "Stop voorlezen" : "Lees voor"}
      className={`inline-flex items-center gap-1.5 rounded-full font-bold text-sm px-3 py-1.5 transition-all active:scale-95 border-2 ${className}`}
      style={
        isThis
          ? { background: "var(--t-accent)", color: "var(--t-on-accent)", borderColor: "var(--t-accent)" }
          : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }
      }
    >
      <Icon className="w-4 h-4" />
      {label != null && <span>{isThis ? "Stop" : label}</span>}
    </button>
  );
}

/** Popover to choose read-aloud voice & speed (persisted in localStorage). */
export function ReadAloudSettings({ "data-testid": testId = "read-settings-btn" }) {
  const { voices, voiceURI, speed, setVoiceURI, setSpeed, speak } = useReadAloud() || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pickVoice = (uri) => { setVoiceURI(uri); speak([{ key: "__preview", text: "Hallo! Zo lees ik voor." }], "g-preview"); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        title="Stem & snelheid"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all active:scale-95"
        style={{ background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}
      >
        <Settings2 className="w-4 h-4" />
      </button>
      {open && (
        <div
          data-testid="read-settings-panel"
          className="absolute right-0 mt-2 w-64 rounded-2xl border-2 p-4 z-50 shadow-xl"
          style={{ background: "var(--t-surface)", borderColor: "var(--t-border)", color: "var(--t-text)" }}
        >
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--t-muted)" }}>Stem</p>
          {voices && voices.length > 0 ? (
            <div className="flex flex-col gap-1.5 mb-4 max-h-40 overflow-y-auto">
              {voices.map((v) => {
                const sel = voiceURI === v.voiceURI;
                return (
                  <button key={v.voiceURI} type="button" data-testid={`voice-${v.voiceURI}`} onClick={() => pickVoice(v.voiceURI)}
                    className="py-2 px-3 rounded-xl text-xs font-bold transition-all active:scale-95 border-2 text-left truncate"
                    style={sel ? { background: "var(--t-accent)", color: "var(--t-on-accent)", borderColor: "var(--t-accent)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                    {v.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs font-medium mb-4" style={{ color: "var(--t-muted)" }} data-testid="no-dutch-voice">
              Geen Nederlandse stem op dit apparaat gevonden — de standaardstem wordt gebruikt.
            </p>
          )}
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--t-muted)" }}>Snelheid</p>
          <div className="grid grid-cols-3 gap-1.5">
            {SPEED_OPTIONS.map((s) => {
              const sel = Number(speed) === s.value;
              return (
                <button key={s.value} type="button" data-testid={`speed-${s.value}`} onClick={() => setSpeed(s.value)}
                  className="py-2 rounded-xl text-xs font-bold transition-all active:scale-95 border-2"
                  style={sel ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
